import { useQuery } from '@tanstack/react-query'

import client from '@/ports/api-client'
import { getProductData } from '@/models/plans/apis'

const getApplicationData = async (applicationId: string) => {
  return client.get(`/applications/${applicationId}`, {
    noLoading: true
  })
}

const getCreditUsageData = async () => {
  return client.get(`/blocks/usages/credit_checks`, {
    noLoading: true
  })
}

const getUsagesData = async (usage: string) => {
  return client.get(`/blocks/usages/${usage}`, {
    noLoading: true
  })
}

const getPaymentMethodData = async () => {
  return client.get(`/plans/payment_methods`, {
    noLoading: true
  })
}

const useGetApplication = (applicationId: string) => {
  const { data } = useQuery({
    queryKey: ['application'],
    queryFn: () => getApplicationData(applicationId),
    refetchOnWindowFocus: false
  })
  return data
}

const useGetCreditUsage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['block'],
    queryFn: () => getCreditUsageData(),
    staleTime: 30000,
    refetchOnWindowFocus: false
  })
  return { data, isLoading }
}

const useGetUsage = (usage = '') => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['blocks', usage],
    queryFn: () => getUsagesData(usage),
    staleTime: 60000 * 5,
    refetchOnWindowFocus: false,
    enabled: !!usage
  })

  return { data, isLoading, refetch }
}

const useGetUsages = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['blocks'],
    queryFn: () => getUsagesData(''),
    staleTime: 30000,
    refetchOnWindowFocus: false
  })

  return { data, isLoading }
}

const useGetPaymentMethod = (key: string) => {
  const { data, isLoading, isFetching, isRefetching, refetch } = useQuery({
    queryKey: [key],
    queryFn: () => getPaymentMethodData(),
    refetchOnWindowFocus: false
  })
  return { data, isLoading, isFetching, isRefetching, refetch }
}

const useGetProduct = (productName: string, opt: { couponCode?: string; state?: string } = {}) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      'products',
      opt?.couponCode
        ? `${productName}-${opt.couponCode}-${opt.state}`
        : `${productName}-${opt.state}`
    ],
    queryFn: () => getProductData(productName, opt),
    refetchOnWindowFocus: false,
    enabled: !!productName
  })
  return { data, isLoading }
}

const useAvailableCreditChecks = () => {
  const { data: usage, isLoading } = useGetUsages()
  const planCreditChecks =
    usage?.blocks.credit_checks.current_value - usage?.blocks.credit_checks.usage.current_value
  const purchasedCreditChecks =
    usage?.blocks.credit_checks_additional_packages.current_value -
    usage?.blocks.credit_checks_additional_packages.usage.current_value
  const availableCreditChecks = planCreditChecks + purchasedCreditChecks
  return {
    availableCreditChecks,
    isLoading,
    creditDetails: {
      planCreditChecks,
      purchasedCreditChecks
    }
  }
}

const useSponsoredListingCreditInformation = () => {
  const { data: usage, isLoading, refetch } = useGetUsage('sponsored_listing_days')

  const purchasedCredits = usage?.blocks?.sponsored_listing_days?.value
  const usedCredits = usage?.blocks?.sponsored_listing_days?.usage?.current_value
  const availableCredits = purchasedCredits - usedCredits

  return { purchasedCredits, usedCredits, availableCredits, isLoading, refetch }
}

export {
  useGetApplication,
  useGetCreditUsage,
  useGetPaymentMethod,
  useGetProduct,
  useGetUsages,
  useGetUsage,
  useAvailableCreditChecks,
  useSponsoredListingCreditInformation
}
